'use client';

import { Button } from '@components/ui/button';
import { Calendar } from '@components/ui/calendar';
import { Checkbox } from '@components/ui/checkbox';
import { Input } from '@components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { format, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';
import { isUnknownDateValue, parseDateFromStorage, UNKNOWN_DATE_VALUE } from '@/utils/date';

// Mask typed input into MM/DD/YYYY as the user types: keep only digits (max 8 →
// MMDDYYYY) and re-insert the "/" separators at the fixed month/day boundaries so
// the slashes always sit in the correct position. A slash is added once the group
// before it is complete (3rd digit → "MM/", 5th digit → "MM/DD/"), which keeps
// backspacing predictable. Final validation/normalization still happens on
// blur/Enter via validateAndSetDate.
function maskDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const groups = [digits.slice(0, 2)];
  if (digits.length > 2) groups.push(digits.slice(2, 4));
  if (digits.length > 4) groups.push(digits.slice(4, 8));
  return groups.join('/');
}

// Given a masked value and a count of digits that sit before the caret, return the
// string index the caret should move to (skipping past a "/" so the next keystroke
// lands in the following group). Keeps the caret stable across reformatting.
function caretIndexForDigitCount(masked: string, digitCount: number): number {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < masked.length; i++) {
    if (/\d/.test(masked[i])) {
      seen++;
      if (seen === digitCount) {
        const next = i + 1;
        return masked[next] === '/' ? next + 1 : next;
      }
    }
  }
  return masked.length;
}

interface DatePickerProps {
  /** Placed on whichever control the user types into, so callers can focus the field by id. */
  id?: string;
  value?: Date | string;
  // `unknown` is true when the user marked the date as "Unknown" (allowUnknown fields).
  // Callers should store UNKNOWN_DATE_VALUE when unknown is true, otherwise the date.
  onChange: (date: Date | undefined, unknown?: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowManualInput?: boolean;
  // When true, render an "Unknown" checkbox that lets the user positively mark the
  // date as unknown (distinct from empty). Opt-in via the nomos allow_unknown flag.
  allowUnknown?: boolean;
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'mm/dd/yyyy',
  disabled = false,
  className,
  allowManualInput = true,
  allowUnknown = false,
}: DatePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(() => {
    if (value && !isUnknownDateValue(value)) {
      // Parse strings through parseDateFromStorage so a naive `yyyy-MM-dd` value
      // is read as a local date (not UTC midnight, which would shift a day).
      return value instanceof Date ? value : parseDateFromStorage(value);
    }
    // Don't auto-select current date if no value provided
    return undefined;
  });

  const [isUnknown, setIsUnknown] = React.useState<boolean>(() => isUnknownDateValue(value));
  const [inputValue, setInputValue] = React.useState<string>('');
  const [hasValidationError, setHasValidationError] = React.useState<boolean>(false);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  // Caret index to restore after a masked reformat sets a new controlled value.
  const pendingCaretRef = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    if (pendingCaretRef.current !== null && inputRef.current) {
      const pos = pendingCaretRef.current;
      inputRef.current.setSelectionRange(pos, pos);
      pendingCaretRef.current = null;
    }
  }, [inputValue]);

  React.useEffect(() => {
    if (isUnknownDateValue(value)) {
      setIsUnknown(true);
      setDate(undefined);
      setInputValue('');
      return;
    }
    setIsUnknown(false);
    if (!value) {
      setDate(undefined);
      setInputValue('');
      return;
    }
    const newDate = value instanceof Date ? value : parseDateFromStorage(value);
    if (newDate && !isNaN(newDate.getTime())) {
      setDate(newDate);
      setInputValue(formatDisplayDate(newDate));
    }
  }, [value, onChange]);

  const handleUnknownToggle = (checked: boolean) => {
    setIsUnknown(checked);
    if (checked) {
      // Clear any typed/selected date and record the unknown sentinel.
      setDate(undefined);
      setInputValue('');
      setHasValidationError(false);
      setIsOpen(false);
      onChange(undefined, true);
    } else {
      onChange(undefined, false);
    }
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    setInputValue(selectedDate ? formatDisplayDate(selectedDate) : '');
    setHasValidationError(false);
    onChange(selectedDate);
    setIsOpen(false); // Close the popover after selecting a date
  };

  const validateAndSetDate = (inputStr: string) => {
    if (!inputStr.trim()) {
      setDate(undefined);
      setHasValidationError(false);
      onChange(undefined);
      return;
    }

    // Normalize on commit: accept fully-typed MM/DD/YYYY, unpadded 1/5/2026, and
    // pure digits (05052001) — the latter covers users who skip the separators.
    const formats = ['MM/dd/yyyy', 'M/d/yyyy', 'MMddyyyy'];

    for (const formatStr of formats) {
      const parsed = parse(inputStr, formatStr, new Date());
      if (isValid(parsed)) {
        setDate(parsed);
        setInputValue(formatDisplayDate(parsed)); // Normalize the display format
        setHasValidationError(false);
        onChange(parsed);
        return;
      }
    }

    // If no format worked, mark as invalid
    setHasValidationError(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const selectionStart = e.target.selectionStart ?? raw.length;
    // Count digits before the caret so it can be restored after reformatting,
    // regardless of any slashes the mask adds or removes.
    const digitsBeforeCaret = raw.slice(0, selectionStart).replace(/\D/g, '').length;

    const masked = maskDateInput(raw);
    pendingCaretRef.current = caretIndexForDigitCount(masked, digitsBeforeCaret);
    setInputValue(masked);
    if (hasValidationError) {
      setHasValidationError(false);
    }
  };

  const handleInputBlur = () => {
    validateAndSetDate(inputValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateAndSetDate(inputValue);
    }
  };

  const formatDisplayDate = (date: Date | undefined) => {
    if (!date) return placeholder;
    // Preview in US MM/dd/yyyy format (stored canonically as yyyy-MM-dd by callers)
    return format(date, 'MM/dd/yyyy');
  };

  const unknownCheckbox = allowUnknown ? (
    <label className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
      <Checkbox
        checked={isUnknown}
        disabled={disabled}
        onCheckedChange={checked => handleUnknownToggle(checked === true)}
      />
      Unknown
    </label>
  ) : null;

  if (!allowManualInput) {
    // Original button-only implementation
    return (
      <div className="w-full">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id={id}
              variant="outline"
              disabled={disabled || isUnknown}
              className={cn(
                'text-sm w-full justify-start text-left font-normal',
                !date && 'text-muted-foreground',
                className,
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {isUnknown ? UNKNOWN_DATE_VALUE : formatDisplayDate(date)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              defaultMonth={date}
              captionLayout="dropdown"
              startMonth={new Date(1900, 0)}
              endMonth={new Date(2100, 11)}
              className="min-w-[250px] rounded-md shadow-sm"
            />
          </PopoverContent>
        </Popover>
        {unknownCheckbox}
      </div>
    );
  }

  // Enhanced implementation with manual input
  return (
    <div className="w-full">
      <div className="relative flex">
        <Input
          id={id}
          ref={inputRef}
          type="text"
          value={isUnknown ? UNKNOWN_DATE_VALUE : inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled || isUnknown}
          maxLength={10}
          inputMode="numeric"
          className={cn(
            'pr-10',
            hasValidationError && 'border-destructive focus-visible:ring-destructive',
            className,
          )}
        />
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled || isUnknown}
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              defaultMonth={date}
              captionLayout="dropdown"
              startMonth={new Date(1900, 0)}
              endMonth={new Date(2100, 11)}
              className="min-w-[250px] rounded-md shadow-xs"
            />
          </PopoverContent>
        </Popover>
      </div>
      {hasValidationError && (
        <p className="mt-1 text-xs text-destructive">
          Please enter a valid date in MM/DD/YYYY format
        </p>
      )}
      {unknownCheckbox}
    </div>
  );
}
